import { FC, PropsWithChildren, useCallback } from 'react';
import { useCreateAiAgentChat } from '@/entities/ai-agent-chat';
import { useHasPermission } from '@/entities/profile';
import { useSettings } from '@/entities/settings';
import { CopilotContext } from '../model/context';
import { useCmdJ } from '../model/useCmdJ';

export const CopilotProvider: FC<PropsWithChildren> = ({ children }) => {
  const { sidebar, setSidebarSettings } = useSettings();
  const view = sidebar.copilotView ?? 'chat';
  const activeChatId = sidebar.copilotActiveChatId ?? null;
  const activePanel = sidebar.activePanel ?? 'navigation';
  const canUseAiAgent = useHasPermission('USE_AI_AGENT');

  const { mutateAsync: createChatApi, isPending: isCreatingChat } = useCreateAiAgentChat();

  const showChatList = useCallback(() => {
    setSidebarSettings({ copilotView: 'list', activePanel: 'copilot' });
  }, [setSidebarSettings]);

  const openChat = useCallback(
    (chatId: number) => {
      setSidebarSettings({ copilotActiveChatId: chatId, copilotView: 'chat' });
    },
    [setSidebarSettings],
  );

  const createNewChat = useCallback(async () => {
    const chat = await createChatApi();
    setSidebarSettings({ copilotActiveChatId: chat.id, copilotView: 'chat', activePanel: 'copilot' });
  }, [createChatApi, setSidebarSettings]);

  const togglePanel = useCallback(() => {
    const nextPanel = activePanel === 'copilot' ? 'navigation' : 'copilot';
    // Без права кнопки «Навигация» нет, и из открытой сочетанием панели было бы не выйти
    if (nextPanel === 'copilot' && !canUseAiAgent) return;
    setSidebarSettings({ activePanel: nextPanel });
  }, [activePanel, canUseAiAgent, setSidebarSettings]);

  const { shortcut } = useCmdJ(togglePanel);

  return (
    <CopilotContext.Provider
      value={{
        view,
        activeChatId,
        isCreatingChat,
        shortcut,
        showChatList,
        openChat,
        createNewChat,
      }}
    >
      {children}
    </CopilotContext.Provider>
  );
};
